import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Users } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { ConsumptionBar } from "../components/ConsumptionBar";
import { StatusBadge } from "../components/StatusBadge";
import { useInsurerConsumption } from "../hooks/useInsurerConsumption";

function shortDate(value: string): string {
  return new Date(value).toLocaleDateString("fr-FR");
}

/** Compte Assurance : fiche d'une famille, consommation cumulée et consommation de chaque membre. */
export function InsurerFamilyPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { families, loading, error } = useInsurerConsumption();
  const family = families.find((row) => row.employee_id === Number(id));
  const listHref = "/dashboard/beneficiaries";

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Breadcrumbs
          homeHref="/dashboard/enterprise"
          items={[{ label: t.safecheck.nav.employeeManagement }, { label: t.safecheck.nav.beneficiaryList, href: listHref }, { label: family?.name ?? "Famille" }]}
        />
        <Link to={listHref} className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground hover:border-accent hover:bg-accent/20">
          <ArrowLeft size={15} aria-hidden="true" />
          Retour
        </Link>
      </div>

      {error ? <p className="mb-3 text-sm text-red-600">{error}</p> : null}
      {loading ? <p className="text-sm text-muted">Chargement de la famille…</p> : null}
      {!loading && !error && !family ? <p className="text-sm text-muted">Famille introuvable.</p> : null}

      {family ? (
        <div className="flex flex-col gap-5">
          <section className="theme-card p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex min-w-0 items-center gap-3">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-accent text-accent-foreground" aria-hidden="true">
                  <Users size={20} strokeWidth={2} />
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted">Fiche de l'agent et de sa famille</p>
                  <h1 className="text-xl font-semibold text-foreground">{family.name}</h1>
                  <p className="text-sm text-muted">
                    {family.company} · {family.package ?? "Sans package"} · {family.members_count} membre(s)
                  </p>
                </div>
              </div>
              <StatusBadge variant="purple">{family.matricule}</StatusBadge>
            </div>

            <div className="mt-5">
              <h2 className="mb-2 text-sm font-semibold text-foreground">Consommation cumulée de la famille</h2>
              <ConsumptionBar consumed={family.consumed} ceiling={family.ceiling} percent={family.percent} />
              <p className="mt-2 text-xs text-muted">
                Année du contrat du {shortDate(family.period.from)} au {shortDate(family.period.to)}. Factures validées et payées.
              </p>
            </div>
          </section>

          <section className="theme-card overflow-hidden">
            <h2 className="border-b border-border px-5 py-4 text-base font-semibold text-foreground">Consommation individuelle</h2>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="border-b border-border bg-card-muted/50 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                    <th className="px-5 py-3">Membre</th>
                    <th className="px-4 py-3">Relation</th>
                    <th className="px-4 py-3">Consommation</th>
                    <th className="px-4 py-3">Fiche</th>
                  </tr>
                </thead>
                <tbody>
                  {family.members.map((member) => (
                    <tr key={member.key} className="border-b border-border last:border-0">
                      <td className="px-5 py-3 text-sm font-semibold text-foreground">{member.name}</td>
                      <td className="px-4 py-3 text-xs font-semibold text-muted-foreground">{member.relation}</td>
                      <td className="px-4 py-3">
                        <ConsumptionBar consumed={member.consumed} ceiling={member.ceiling} percent={member.percent} />
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={member.type === "employee" ? `/dashboard/employees/${member.id}` : `/dashboard/beneficiaries/dependant/${member.id}`}
                          className="text-sm text-primary hover:underline"
                        >
                          Voir
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
}
